// src/services/api.js
import axiosInstance from './axiosInstance';
import { tokenManager } from './tokenManager'; 
import moment from 'moment'; // Added for moment.js
import axios from 'axios'; // Added for axios

// ============================================================================
// AUTHENTICATION & NAVIGATION HELPERS
// ============================================================================

export const handleAuthSuccess = (response, navigate) => {
  // Set tokens if available
  if (response.access_token && response.refresh_token) {
    tokenManager.setTokens(response.access_token, response.refresh_token);
  }

  // Get user data from the correct location in response
  const userData = response.data?.user || response.user || response;
  console.log('Auth Success - User Data:', userData);

  // Set default role as patient if not specified
  const userWithRole = {
    ...userData,
    role: userData.role || 'patient'
  };
  
  // Store user info
  tokenManager.setUser(userWithRole);
  
  // Check for pending booking after login
  const pendingBooking = sessionStorage.getItem('pendingBooking');
  if (pendingBooking) {
    const bookingData = JSON.parse(pendingBooking);
    sessionStorage.removeItem('pendingBooking');
    navigate('/payment', { state: bookingData });
    return;
  }

  // Navigate based on role
  switch(userWithRole.role) {
    case 'admin':
      navigate('/admin/dashboard');
      break;
    case 'psychiatrist':
      navigate('/psy/dashboard');
      break;
    case 'nurse':
      navigate('/nurse/dashboard');
      break;
    default:
      navigate("/dashboard");
  }
};

// Handle logout and redirect
export const handleLogout = async () => {
  await authAPI.logout();
};

// ============================================================================
// BOOKING FLOW UTILITIES
// ============================================================================

// Save pending booking data for later use
export const savePendingBooking = (bookingData) => {
  sessionStorage.setItem('pendingBooking', JSON.stringify(bookingData));
};

// Get pending booking data
export const getPendingBooking = () => {
  const pending = sessionStorage.getItem('pendingBooking');
  return pending ? JSON.parse(pending) : null;
};

// Clear pending booking data
export const clearPendingBooking = () => {
  sessionStorage.removeItem('pendingBooking');
};

// Check if user can proceed with booking
export const canProceedWithBooking = () => {
  const isAuthenticated = tokenManager.isAuthenticated();
  const user = tokenManager.getUser();
  return isAuthenticated && user;
};

// ============================================================================
// AUTH APIs
// ============================================================================

export const authAPI = {
  register: async (userData) => {
    console.log('Registering with data:', userData);
    const res = await axiosInstance.post('/users/signup', userData);
    console.log('Register API Response:', res.data);
    return res.data;
  },

  login: async (email, password) => {
    const res = await axiosInstance.post('/users/login', { email, password });
    return res.data;
  },

  googleAuth: async (credential) => {
    const res = await axiosInstance.post('/users/google-auth', { credential });
    return res.data;
  },

  logout: async () => {
    try {
      // Call logout endpoint
      await axiosInstance.post('/users/logout', {});
    } catch (error) {
      // Even if logout API fails, still clear local data
      console.error('Logout API error:', error);
    } finally {
      // Always clear all localStorage data
      tokenManager.clearTokens();
      
      // Redirect to login page
      window.location.href = '/';
    }
  },

  refreshToken: async () => {
    const refreshToken = tokenManager.generateRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const res = await axiosInstance.post('/users/refresh-token', {
      refresh_token: refreshToken
    });
    
    const { access_token } = res.data;
    tokenManager.setTokens(access_token);

    return res.data;
  },

  forgotPassword: async (email) => {
    const res = await axiosInstance.post('/users/forgot-password', { email });
    return res.data;
  },

  verifyResetToken: async (token) => {
    const res = await axiosInstance.post('/users/verify-reset-token', { token });
    return res.data;
  },

  resetPassword: async (token, newPassword) => {
    const res = await axiosInstance.post('/users/reset-password', { token, newPassword });
    return res.data;
  },

  // Get user and payment details by session ID (for payment success)
  getPaymentSuccessData: async (sessionId) => {
    const res = await axiosInstance.get(`/payments/session-user/${sessionId}`);
    return res.data;
  },
};

// ============================================================================
// USER MANAGEMENT APIs
// ============================================================================

const baseURL = process.env.REACT_APP_API_BASE_URL || 'https://backend.adhdrapidcare.com/api';

// Standard headers
const headers = {
  'Content-Type': 'application/json',
};


export const userAPI = {
  getAllUsers: async (page = 1, limit = 10) => {
    const res = await axiosInstance.get(`/users?page=${page}&limit=${limit}`);
    return res.data;
  },



  
  getAllNurses: async () => {
    const res = await axios.get(`${baseURL}/users/nurses`, {
      headers,
    });
    return res.data;
  },

  getNurseById: async (id) => {
    const res = await axios.get(`${baseURL}/users/nurse/${id}`, {
      headers,
    });
    return res.data;
  },




  filterUsers: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const res = await axiosInstance.get(`/users/filter?${queryParams.toString()}`);
    return res.data;
  },

  getUsersByRole: async (role, page = 1, limit = 10) => {
    const res = await axiosInstance.get(`/users/role/${role}?page=${page}&limit=${limit}`);
    return res.data;
  },

  getUserById: async (id) => {
    const res = await axiosInstance.get(`/users/${id}`);
    return res.data;
  },

  // Get doctors list (psychiatrists) with pagination
  getDoctorsList: async (page = 1, limit = 10) => {
    const res = await axiosInstance.get(`/users/doctors?page=${page}&limit=${limit}`);
    return res.data;
  },
  
  // Get individual doctor details by ID
  getDoctorById: async (id) => {
    const res = await axiosInstance.get(`/users/doctors/${id}`);
    return res.data;
  },
  getNurseById: async (id) => {
    const res = await axiosInstance.get(`/users/nurse/${id}`);
    return res.data;
  },

  getNursesList: async (page = 1, limit = 10) => {
    const res = await axiosInstance.get(`/users/nurses?page=${page}&limit=${limit}`);
    return res.data;
  },
};

// ============================================================================
// ADMIN APIs
// ============================================================================

export const adminAPI = {
  getAllUsers: async (page = 1, limit = 10) => {
    const res = await axiosInstance.get(`/users?page=${page}&limit=${limit}`);
    return res.data;
  },

  filterUsers: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const res = await axiosInstance.get(`/users/filter?${queryParams.toString()}`);
    return res.data;
  },

  getUsersByRole: async (role, page = 1, limit = 10) => {
    const res = await axiosInstance.get(`/users/role/${role}?page=${page}&limit=${limit}`);
    return res.data;
  },

  getUserById: async (id) => {
    const res = await axiosInstance.get(`/users/${id}`);
    return res.data;
  },

  getProfile: async () => {
    // For admin profile, we can get current user info from token
    const user = tokenManager.getUser();
    return { user };
  },
    getDashboardStats: async () => {
    const res = await axiosInstance.get('/admin/dashboard/stats');
    return res.data;
  },

  getRecentSessions: async () => {
    const res = await axiosInstance.get('/admin/dashboard/recent-sessions');
    return res.data;
  },

  getRecentPayments: async () => {
    const res = await axiosInstance.get('/admin/dashboard/recent-payments');
    return res.data;
  },

  getRecentPaymentsDetailed: async (filters = {}) => {
    const queryParams = new URLSearchParams();
    
    // Add filter parameters
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = `/admin/dashboard/recent-payments-detailed${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const res = await axiosInstance.get(url);
    return res.data;
  },

  getWeeklySessions: async () => {
    const res = await axiosInstance.get('/admin/dashboard/weekly-sessions');
    return res.data;
  },

  getPerformanceOverview: async () => {
    const res = await axiosInstance.get('/admin/dashboard/performance-overview');
    return res.data;
  },

  getPaymentSummary: async () => {
    const res = await axiosInstance.get('/admin/dashboard/payment-summary');
    return res.data;
  },

  getNurseProfile: async (nurseId) => {
    const res = await axiosInstance.get(`/admin/nurse/${nurseId}/profile`);
    return res.data;
  },
};

// ============================================================================
// PATIENT APIs
// ============================================================================

export const patientAPI = {
  createProfile: (profileData) => axiosInstance.post('/patients/profile', profileData).then(res => res.data),
  getMyProfile: async () => {
    const res = await axiosInstance.get('/patients/my-profile');
    return res.data;
  },
  updateProfile: async (profileData) => {
    const res = await axiosInstance.put('/patients/my-profile', profileData, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return res.data;
  },
  uploadProfileImage: async (formData) => {
    const res = await axiosInstance.post('/patients/upload-profile-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return res.data;
  },
  getNotifications: async (userId) => {
    const res = await axiosInstance.get(`/patients/${userId}/notifications`);
    return res.data;
  },
  getSessions: async (patientId) => {
    const res = await axiosInstance.get(`/patients/${patientId}/sessions`);
    return res.data.sessions;
  },
  getAssignedPsychiatrist: async (patientId) => {
    const res = await axiosInstance.get(`/patients/${patientId}/assigned-psychiatrist`);
    return res.data;
  },

  // Referral functions
  getReferralStatus: async () => {
    const res = await axiosInstance.get('/referrals/status');
    return res.data;
  },

  getPendingReferrals: async () => {
    const res = await axiosInstance.get('/referrals/pending');
    return res.data;
  },
  diagnosisHistory: async (diagnosisData) => {
    const res = await axiosInstance.post('/diagnosis-history', diagnosisData);
    return res.data;
  },
  verifyReferral: async (referralId, notes) => {
    const res = await axiosInstance.post(`/referrals/verify/${referralId}`, { notes });
    return res.data;
  },

  rejectReferral: async (referralId, reason, notes) => {
    const res = await axiosInstance.post(`/referrals/reject/${referralId}`, { reason, notes });
    return res.data;
  },

  uploadReferral: async (formData) => {
    const res = await axiosInstance.post('/referrals/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    });
    return res.data;
  },

  downloadReferralDocument: async (referralId) => {
    const res = await axiosInstance.get(`/referrals/document/${referralId}`, {
      responseType: 'blob'
    });
    
    // Create blob URL and trigger download
    const blob = new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Extract filename from response headers
    const contentDisposition = res.headers['content-disposition'];
    let filename = 'referral-document.pdf';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="(.+)"/);
      if (filenameMatch) filename = filenameMatch[1];
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return res.data;
  },

  // Get assigned nurse details
  getAssignedNurse: async (patientId) => {
    try {
      const res = await axiosInstance.get(`/patients/${patientId}/assigned-nurse`);
      return res.data;
    } catch (error) {
      console.error('Error fetching assigned nurse:', error);
      throw error;
    }
  },

  // Get diagnosis histories for a patient
  getDiagnosisHistories: async (patientId) => {
    try {
      const res = await axiosInstance.get(`/diagnosis-history/patient/${patientId}`);
      return res.data;
    } catch (error) {
      if (error.response?.status === 404) {
        // Return null if no diagnosis found
        return null;
      }
      console.error('Error fetching diagnosis histories:', error);
      throw error;
    }
  },

  getDiagnosisHistory: async (patientId) => {
    try {
      const response = await axiosInstance.get(`/diagnosis-history/patient/${patientId}`);
      return response.data;
    } catch (error) {
      handleAPIError(error);
      throw error;
    }
  },

  submitSessionFeedback: async (sessionId, feedbackData) => {
    try {
      const response = await axiosInstance.post(`/nurse-sessions/${sessionId}/feedback`, feedbackData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },
};

// ============================================================================
// AVAILABILITY APIs
// ============================================================================

export const availabilityAPI = {
  getAvailableSlots: async (userId, date) => {
    const res = await axiosInstance.get(
      `/availability/slots?userId=${userId}&date=${date}`
    );
    return res.data;
  },

  // Get availability by user ID for doctor/nurse settings
  getAvailabilityByUserId: async (userId) => {
    const res = await axiosInstance.get(`/availability/user/${userId}`);
    return res.data;
  },

  // Create or update availability (psychiatrist/nurse)
  createDoctorAvailability: async (availabilityData) => {
    const res = await axiosInstance.post('/availability', availabilityData);
    return res.data;
  },

  // Add availability for a specific date
  addDateAvailability: async (dateAvailabilityData) => {
    const res = await axiosInstance.post('/availability/date', dateAvailabilityData);
    return res.data;
  },

  // Get availability for a date range
  getDateAvailability: async (userId, startDate, endDate) => {
    const res = await axiosInstance.get(`/availability/date?userId=${userId}&startDate=${startDate}&endDate=${endDate}`);
    return res.data;
  },

  // Update existing availability
  updateDoctorAvailability: async (userId, availabilityData) => {
    const res = await axiosInstance.put(`/availability/${userId}`, availabilityData);
    return res.data;
  },

  // Get session preferences for a doctor/nurse
  getSessionPreferences: async (userId) => {
    const res = await axiosInstance.get(`/availability/session-preferences/${userId}`);
    return res.data;
  },

  // Save session preferences for a doctor/nurse
  saveSessionPreferences: async (sessionPreferencesData) => {
    const res = await axiosInstance.post('/availability/session-preferences', sessionPreferencesData);
    return res.data;
  },

  // Update session preferences for a doctor/nurse
  updateSessionPreferences: async (userId, sessionPreferencesData) => {
    const res = await axiosInstance.put(`/availability/session-preferences/${userId}`, sessionPreferencesData);
    return res.data;
  },

  getAvailableSlotsForRange: async (userId, dateFrom, dateTo) => {
    const res = await axiosInstance.get(
      `/availability/slots/range?userId=${userId}&dateFrom=${dateFrom}&dateTo=${dateTo}`
    );
    return res.data;
  },
};

// ============================================================================
// BOOKING APIs
// ============================================================================

export const bookingAPI = {
  // Book a slot (create a session)
  bookSlot: async (bookingData) => {
    console.log('Booking slot with data:', bookingData);
    
    // Determine if this is a nurse or doctor session
    const isNurseSession = bookingData.nurseId;
    const endpoint = isNurseSession ? '/nurse-sessions' : '/availability/book';
    
    // Format the data consistently
    const formattedData = {
      ...bookingData,
      // For nurse sessions, ensure we have the right field names and enforce 20-minute duration
      ...(isNurseSession && {
        nurseId: bookingData.nurseId,
        patientId: bookingData.patientId,
        date: bookingData.date,
        startTime: bookingData.startTime,
        endTime: moment(bookingData.startTime, 'h:mm A').add(20, 'minutes').format('h:mm A'),
        sessionType: bookingData.sessionType || 'monthly_checkin',
        sessionMode: bookingData.sessionMode || 'video',
        durationMinutes: 20 // Always 20 minutes for nurse sessions
      }),
      // For doctor sessions, ensure we have the right field names
      ...(!isNurseSession && {
        doctorId: bookingData.doctorId,
        patientId: bookingData.patientId,
        date: bookingData.date,
        startTime: bookingData.startTime,
        endTime: bookingData.endTime
      })
    };

    try {
      const res = await axiosInstance.post(endpoint, formattedData);
      console.log('Booking response:', res.data);
      
      // Ensure the response has the expected format
      if (!res.data?.session?.id) {
        console.error('Invalid booking response format:', res.data);
        throw new Error('Invalid response format from server');
      }
      
      return res.data;
    } catch (error) {
      console.error('Booking error:', error);
      throw error;
    }
  },

  // Fetch session details by session ID
  getSessionById: async (sessionId) => {
    try {
      // Step 1: Validate the sessionId input
      if (!sessionId || typeof sessionId !== 'string') {
        throw new Error('Invalid session ID: must be a non-empty string');
      }

      // Step 2: Clean and validate sessionId
      const cleanSessionId = sessionId.replace(/[^0-9a-fA-F]/g, '');
      console.log('Original session ID:', sessionId);
      console.log('Cleaned session ID:', cleanSessionId);

      if (cleanSessionId.length !== 24) {
        throw new Error('Invalid session ID format: must be 24 hex characters');
      }

      // Step 3: Call backend API
      const res = await axiosInstance.get(`/nurse-sessions/${cleanSessionId}`);
      console.log('Session response:', res.data);

      // Step 4: Validate structure
      if (!res.data || !res.data.session) {
        throw new Error('Invalid response format from server');
      }

      const session = res.data.session;

      // Step 5: Normalize the session object with defaults
      return {
        session: {
          id: session.id || cleanSessionId,
          doctor: {
            id: session.doctor?.id || '',
            name: session.doctor?.name || 'Unknown',
            email: session.doctor?.email || '',
            profileImage: session.doctor?.profileImage || '',
            role: session.doctor?.role || 'nurse'
          },
          patient: {
            id: session.patient?.id || '',
            name: session.patient?.name || 'Unknown',
            email: session.patient?.email || ''
          },
          date: session.date || new Date().toISOString().split('T')[0],
          startTime: session.startTime || '9:00 AM',
          endTime: session.endTime || '9:30 AM',
          duration: session.duration || 30,
          status: session.status || 'scheduled',
          sessionType: session.sessionType || 'monthly_checkin',
          sessionMode: session.sessionMode || 'video',
          scheduledAt: session.scheduledAt || null
        }
      };
    } catch (error) {
      console.error('Error fetching session:', error);

      // Handle HTTP status errors
      if (error.response) {
        const status = error.response.status;
        const details = error.response?.data?.details || error.message;

        if (status === 404) {
          throw new Error('Session not found');
        } else if (status === 400) {
          throw new Error('Invalid session ID format');
        } else if (status === 500) {
          throw new Error('Server error: ' + details);
        }
      }

      // Default fallback error
      throw new Error('Failed to fetch session: ' + error.message);
    }
  }

};

// ============================================================================
// PAYMENT APIs
// ============================================================================

export const paymentAPI = {
  createCheckoutSession: async (data) => {
    try {
      const res = await axiosInstance.post('/payments/create-checkout-session', data);
      return res.data;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  },
  getLatestPaymentForPatient: async (userId) => {
    const res = await axiosInstance.get(`/payments/patient/${userId}/latest`);
    return res.data;
  },
  createReschedulePayment: async ({ sessionId, priceId, amount }) => {
    const res = await axiosInstance.post('/payments/create-reschedule-session', {
      sessionId,
      priceId,
      amount,
      cancelUrl: `${window.location.origin}/dashboard`
    });
    return res.data;
  },
  getPaymentBySessionId: async (sessionId) => {
    const res = await axiosInstance.get(`/payments/session/${sessionId}`);
    return res.data;
  },

  cancelSubscription: async () => {
    try {
      const res = await axiosInstance.post('/payments/cancel-subscription');
      return res.data;
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  },
};

// ============================================================================
// RECEIPT APIs  
// ============================================================================

export const receiptAPI = {
  getPatientReceipts: async () => {
    const res = await axiosInstance.get('/receipts/patient');
    return res.data;
  },
  
  // Get patient's official service receipts (Stage 4.2)
  getPatientOfficialReceipts: async () => {
    const res = await axiosInstance.get('/official-receipts');
    return res.data;
  },
  
  downloadReceiptPDF: async (receiptId) => {
    const res = await axiosInstance.get(`/receipts/${receiptId}/download`, {
      responseType: 'blob'
    });
    
    // Create blob URL and trigger download
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `payment-receipt-${receiptId}.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return res.data;
  },
  
  // Download official service receipt with proper filename
  downloadOfficialReceiptPDF: async (receiptId, filename) => {
    const res = await axiosInstance.get(`/official-receipts/${receiptId}/download`, {
      responseType: 'blob'
    });
    
    // Create blob URL and trigger download
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'OfficialServiceReceipt.pdf';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return res.data;
  },
  
  getReceiptByPayment: async (paymentId) => {
    const res = await axiosInstance.get(`/receipts/payment/${paymentId}`);
    return res.data;
  }
};

// ============================================================================
// SESSION APIs
// ============================================================================

export const sessionAPI = {
  getAdminSessions: async (page = 1, limit = 10, filters = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      
      // Add filters
      if (filters.patientName) queryParams.append('patientName', filters.patientName);
      if (filters.nurseId) queryParams.append('nurseId', filters.nurseId);
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.sessionType) queryParams.append('sessionType', filters.sessionType);
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      
      console.log('API Query Params:', queryParams.toString());
      
      const res = await axiosInstance.get(`/admin/sessions?${queryParams.toString()}`);
      return res.data;
    } catch (error) {
      console.error('Error fetching admin sessions:', error);
      throw error;
    }
  },

  filterSessions: async (filters = {}) => {
    const queryParams = new URLSearchParams();

    // Map frontend filter names to backend expected parameter names
    if (filters.patient) queryParams.append('patientName', filters.patient);
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.type) queryParams.append('type', filters.type);
    if (filters.doctorId) queryParams.append('doctorId', filters.doctorId);
    if (filters.startDate) queryParams.append('dateFrom', filters.startDate);
    if (filters.endDate) queryParams.append('dateTo', filters.endDate);
    
    // Add pagination parameters
    if (filters.page) queryParams.append('page', filters.page);
    if (filters.limit) queryParams.append('limit', filters.limit);

    const url = `/admin/sessions?${queryParams.toString()}`;

    const res = await axiosInstance.get(url);
    return res.data;
  },

  // Get calendar sessions for doctor
  getCalendarSessions: async (dateFrom, dateTo, doctorId = null, status = null) => {
    const queryParams = new URLSearchParams();
    queryParams.append('dateFrom', dateFrom);
    queryParams.append('dateTo', dateTo);
    
    if (doctorId) queryParams.append('doctorId', doctorId);
    if (status) queryParams.append('status', status);

    const res = await axiosInstance.get(`/admin/sessions/calendar?${queryParams.toString()}`);
    return res.data;
  },

  // Get session statistics for overview
  getSessionStats: async () => {
    const res = await axiosInstance.get("/admin/sessions/overview");
    return res.data;
  },

  // Get today's schedule
  getTodaySchedule: async () => {
    const res = await axiosInstance.get("/admin/sessions/today");
    return res.data;
  },

  // Update session status
  updateSessionStatus: async (sessionId, status) => {
    const res = await axiosInstance.patch(`/admin/sessions/${sessionId}/status`, { status });
    return res.data;
  },

  // Get session by ID
  getSessionById: async (sessionId) => {
    const res = await axiosInstance.get(`/admin/sessions/${sessionId}`);
    return res.data;
  },

  // Get sessions for a specific patient by userId
  getSessionsForPatient: async (userId, patientName) => {
    // patientName is required for filtering, so fetch user profile if not provided
    let name = patientName;
    if (!name) {
      // Try to get user profile from backend or tokenManager
      const user = tokenManager.getUser();
      name = user?.name;
    }
    const queryParams = new URLSearchParams();
    if (name) queryParams.append('patientName', name);
    // Optionally, you can add more filters if needed
    const url = `/sessions?${queryParams.toString()}`;
    const res = await axiosInstance.get(url);
    // Return the sessions array
    return res.data?.data?.sessions || [];
  },

  // Get sessions for a specific doctor by doctor ID (with filters, pagination, sorting)
  getSessionsByDoctorId: async (doctorId, filters = {}) => {
    const queryParams = new URLSearchParams();
    // Add filter parameters
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    const url = `/admin/sessions/doctor/${doctorId}?${queryParams.toString()}`;
    const res = await axiosInstance.get(url);
    return res.data;
  },

  // Fallback function using the existing today sessions endpoint
  getTodaySessionsByDoctorId: async (doctorId) => {
    const res = await axiosInstance.get(`/doctor/${doctorId}/today-sessions`);
    return res.data;
  },

  // Complete consultation with form data
  completeConsultation: async (sessionId, formData) => {
    const res = await axiosInstance.post(`/admin/sessions/${sessionId}/complete`, formData);
    return res.data;
  },

  getAIAnalysis: async (sessionId) => {
    const res = await axiosInstance.get(`/admin/sessions/${sessionId}/ai-analysis`);
    return res.data;
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

// Create booking data object
export const createBookingData = (doctorId, patientId, date, startTime, endTime) => {
  return {
    doctorId,
    patientId,
    date: typeof date === 'string' ? date : date.format('YYYY-MM-DD'),
    startTime,
    endTime,
  };
};

// Handle API errors consistently
export const handleAPIError = (error, defaultMessage = 'An error occurred') => {
  console.error('API Error:', error);
  
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error.message) {
    return error.message;
  }
  
  return defaultMessage;
};

// ============================================================================
// COMPOSITE FUNCTIONS (High-level operations)
// ============================================================================

// Complete booking flow
export const completeBookingFlow = async (bookingData, paymentType, navigate) => {
  try {
    // Get current user
    const user = tokenManager.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Always use latest booking data from sessionStorage if available
    const pendingBooking = getPendingBooking();
    const finalBookingData = pendingBooking || bookingData;

    // Create checkout session, pass user email for Stripe autofill
    const checkoutResponse = await paymentAPI.createCheckoutSession({
      type: paymentType,
      patientId: user.id || user._id,
      bookingData: finalBookingData,
      email: user.email, // Pass email for autofill
      successUrl: `${window.location.origin}/payment/success?stripe_session_id={CHECKOUT_SESSION_ID}&booking=${encodeURIComponent(JSON.stringify(finalBookingData))}`,
      cancelUrl: `${window.location.origin}/payment/cancel`
    });

    // Redirect to Stripe checkout
    window.location.href = checkoutResponse.url;

  } catch (error) {
    throw new Error(handleAPIError(error, 'Failed to initiate booking'));
  }
};

// Process payment success and book session
export const processPaymentSuccess = async (bookingData, navigate) => {
  try {
    // Get current user
    const user = tokenManager.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Always use latest booking data from sessionStorage if available
    const pendingBooking = getPendingBooking();
    const finalBookingData = pendingBooking || bookingData;

    // Book the session
    const bookingResponse = await bookingAPI.bookSlot({
      doctorId: finalBookingData.doctorId,
      patientId: user.id || user._id,
      date: finalBookingData.date,
      startTime: finalBookingData.startTime,
      endTime: finalBookingData.endTime,
    });

    clearPendingBooking(); // Clear after use

    // Return the booking response with session ID
    return {
      ...bookingResponse,
      sessionId: bookingResponse.session?.id
    };

  } catch (error) {
    throw new Error(handleAPIError(error, 'Failed to complete booking'));
  }
};

// Doctor APIs
export const doctorAPI = {
  // Get patients for a specific doctor
  getPatientsForDoctor: async (doctorId, filters = {}) => {
    const queryParams = new URLSearchParams();
    
    // Add filter parameters
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = `/doctor/patient/${doctorId}?${queryParams.toString()}`;
    const res = await axiosInstance.get(url);
    return res.data;
  },

  getDashboardMetrics: async (doctorId) => {
    const res = await axiosInstance.get(`/doctor/${doctorId}/dashboard-metrics`);
    return res.data;
  },
 
  // Get assessments for a specific doctor
  getAssessmentsByDoctorId: async (doctorId, filters = {}) => {
    const queryParams = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = `/doctor/${doctorId}/assessments?${queryParams.toString()}`;
    const res = await axiosInstance.get(url);
    return res.data;
  },

  // Get prescriptions for a specific doctor
  getPrescriptionsByDoctorId: async (doctorId, filters = {}) => {
    const queryParams = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = `/doctor/${doctorId}/prescriptions?${queryParams.toString()}`;
    const res = await axiosInstance.get(url);
    return res.data;
  },

  // Get today's sessions for a specific doctor
  getTodaySessionsByDoctorId: async (doctorId) => {
    const res = await axiosInstance.get(`/doctor/${doctorId}/today-sessions`);
    return res.data;
  },
};

// ============================================================================
// NURSE APIs
// ============================================================================

export const nurseAPI = {
  // Get nurse schedule
  getNurseSchedule: async (nurseId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add any query parameters
      Object.keys(params).forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const url = `/nurse-sessions/nurse/${nurseId}?${queryParams.toString()}`;
      const res = await axiosInstance.get(url);
      return res.data;
    } catch (error) {
      console.error('Error fetching nurse schedule:', error);
      throw error;
    }
  },

  // Get nurse sessions for a patient
  getPatientNurseSessions: async (patientId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filters
      if (params.status && params.status !== 'All') {
        queryParams.append('status', params.status.toLowerCase());
      }
      if (params.search) {
        queryParams.append('search', params.search);
      }

      const url = `/nurse-sessions/patient/${patientId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const res = await axiosInstance.get(url);
      return res.data;
    } catch (error) {
      console.error('Error fetching patient nurse sessions:', error);
      throw error;
    }
  },

  // Get a specific nurse session
  getNurseSession: async (sessionId) => {
    try {
      const res = await axiosInstance.get(`/nurse-sessions/${sessionId}`);
      return res.data;
    } catch (error) {
      console.error('Error fetching nurse session:', error);
      throw error;
    }
  },

  // Update nurse session status
  updateNurseSessionStatus: async (sessionId, status) => {
    try {
      const res = await axiosInstance.patch(`/nurse-sessions/${sessionId}/status`, { status });
      return res.data;
    } catch (error) {
      console.error('Error updating nurse session status:', error);
      throw error;
    }
  },

  // Complete nurse session with notes
  completeNurseSession: async (sessionId, completionData) => {
    try {
      const res = await axiosInstance.post(`/nurse-sessions/${sessionId}/complete`, completionData);
      return res.data;
    } catch (error) {
      console.error('Error completing nurse session:', error);
      throw error;
    }
  },

  // Update nurse session
  updateNurseSession: async (sessionId, updateData) => {
    try {
      const res = await axiosInstance.put(`/nurse-sessions/${sessionId}`, updateData);
      return res.data;
    } catch (error) {
      console.error('Error updating nurse session:', error);
      throw error;
    }
  },

  // Update nurse session time slot
  updateSessionTimeSlot: async (sessionId, updateData) => {
    try {
      const res = await axiosInstance.put(`/nurse-sessions/${sessionId}/time-slot`, updateData);
      return res.data;
    } catch (error) {
      console.error('Error updating session time slot:', error);
      throw error;
    }
  },

  // Get nurse dashboard stats
  getNurseDashboardStats: async (nurseId) => {
    try {
      const response = await axiosInstance.get(`/nurse-sessions/nurse/${nurseId}/dashboard`);
      return response.data;
    } catch (error) {
      handleAPIError(error);
      throw error;
    }
  },

  // Get nurse's assigned patients
  getNurseAssignedPatients: async (nurseId, params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      
      // Add pagination parameters
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Add filters
      if (params.search) queryParams.append('search', params.search);
      if (params.diagnosis) queryParams.append('diagnosis', params.diagnosis);
      if (params.subscription_status && params.subscription_status !== 'all') {
        queryParams.append('subscription_status', params.subscription_status);
      }

      const url = `/nurse-sessions/nurse/${nurseId}/patients${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      handleAPIError(error);
      throw error;
    }
  },

  addSessionNotes: async (sessionId, data) => {
    const res = await axiosInstance.post(`/nurse-sessions/${sessionId}/notes`, data);
    return res.data;
  },

  // Get nurse profile
  getNurseProfile: async () => {
    try {
      const response = await axiosInstance.get('/nurses/profile');
      return response.data;
    } catch (error) {
      console.error('Error fetching nurse profile:', error);
      throw error;
    }
  },

  // Update nurse profile
  updateNurseProfile: async (profileData) => {
    try {
      const response = await axiosInstance.post('/nurses/profile', profileData);
      return response.data;
    } catch (error) {
      console.error('Error updating nurse profile:', error);
      throw error;
    }
  },
};


export const userProfileAPI = {
  // Get current user profile
  getProfile: async () => {
    const res = await axiosInstance.get('/users/profile');
    return res.data;
  },

  // Update user profile
  updateProfile: async (profileData) => {
    const res = await axiosInstance.put('/users/profile', profileData);
    return res.data;
  },

  // Update profile image
  updateProfileImage: async (imageFile) => {
    const formData = new FormData();
    formData.append('profileImage', imageFile);
    
    const res = await axiosInstance.put('/users/profile/image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return res.data;
  },

  // Change password
  changePassword: async (currentPassword, newPassword) => {
    const res = await axiosInstance.put('/users/change-password', {
      currentPassword,
      newPassword,
    });
    return res.data;
  },

  // Get user settings
  getSettings: async () => {
    const res = await axiosInstance.get('/users/settings');
    return res.data;
  },

  // Update user settings
  updateSettings: async (settings) => {
    const res = await axiosInstance.put('/users/settings', settings);
    return res.data;
  },
};

// ============================================================================
// PRESCRIPTION APIs
// ============================================================================

export const prescriptionAPI = {
  // Create a new prescription
  createPrescription: async (prescriptionData) => {
    const doctorId = tokenManager.getUser()?._id || tokenManager.getUser()?.id;
    if (!doctorId) {
      throw new Error('Doctor ID not found. Please log in again.');
    }
    
    const res = await axiosInstance.post(`/doctor/${doctorId}/prescriptions`, prescriptionData);
    return res.data;
  },

  // Get prescriptions for a specific doctor
  getPrescriptionsByDoctor: async (doctorId, filters = {}) => {
    const queryParams = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = `/doctor/${doctorId}/prescriptions?${queryParams.toString()}`;
    const res = await axiosInstance.get(url);
    return res.data;
  },

  // Get prescriptions for a specific patient
  getPatientPrescriptions: async (patientId, filters = {}) => {
    const queryParams = new URLSearchParams();
    
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });

    const url = `/patient/${patientId}/prescriptions?${queryParams.toString()}`;
    const res = await axiosInstance.get(url);
    return res.data;
  },

  // Get prescription by ID
  getPrescriptionById: async (prescriptionId) => {
    const res = await axiosInstance.get(`/prescriptions/${prescriptionId}`);
    return res.data;
  },

  // Update prescription
  updatePrescription: async (prescriptionId, updateData) => {
    const res = await axiosInstance.put(`/prescriptions/${prescriptionId}`, updateData);
    return res.data;
  },

  // Delete prescription
  deletePrescription: async (prescriptionId) => {
    const res = await axiosInstance.delete(`/prescriptions/${prescriptionId}`);
    return res.data;
  },

  // Request refill for prescription
  requestRefill: async (prescriptionId) => {
    const res = await axiosInstance.post(`/prescriptions/${prescriptionId}/refill`);
    return res.data;
  },

  // Get prescription statistics for doctor
  getPrescriptionStats: async (doctorId) => {
    const res = await axiosInstance.get(`/doctor/${doctorId}/prescriptions/stats`);
    return res.data;
  },
};

export const assessmentAPI = {
  // Get assessments for a doctor, with optional status and risk filters
  getAssessmentsForDoctor: async (doctorId, { status, risk } = {}) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (risk) params.append('risk', risk);
    const url = `/assessment/doctor/${doctorId}${params.toString() ? '?' + params.toString() : ''}`;
    const res = await axiosInstance.get(url);
    return res.data;
  },

  // Get a single assessment by its ID
  getAssessmentById: async (assessmentId) => {
    const res = await axiosInstance.get(`/assessment/${assessmentId}`);
    return res.data;
  },
// Get assessment by patient ID
  getAssessmentByPatientId: async (patientId) => {
    if (!patientId) {
      throw new Error('Patient ID is required');
    }
    
    const url = `/assessment/patient/${patientId}`;
    const res = await axiosInstance.get(url);
    return res.data;
  },
  // Create or update an assessment
  createOrUpdateAssessment: async (assessmentData) => {
    const res = await axiosInstance.post('/assessment', assessmentData);
    return res.data;
  },
  
  // Alias for createOrUpdateAssessment for compatibility
  submitAssessment: async (assessmentData) => {
    const res = await axiosInstance.post('/assessment', assessmentData);
    return res.data;
  },

      
};

// Monthly Assessment APIs
export const monthlyAssessmentAPI = {
  // Create monthly assessment
  createMonthlyAssessment: async (assessmentData) => {
    try {
      // Get session ID from URL
      const sessionId = window.location.pathname.split('/').pop();
      const res = await axiosInstance.post(`/monthly-assessments/${sessionId}`, assessmentData);
      return res.data;
    } catch (error) {
      console.error('Error creating monthly assessment:', error);
      throw error;
    }
  },

  // Get monthly assessment by ID
  getMonthlyAssessmentById: async (assessmentId) => {
    try {
      const res = await axiosInstance.get(`/monthly-assessments/${assessmentId}`);
      return res.data;
    } catch (error) {
      console.error('Error fetching monthly assessment:', error);
      throw error;
    }
  },

  // Get patient's monthly assessments
  getPatientMonthlyAssessments: async (patientId) => {
    try {
      const res = await axiosInstance.get(`/monthly-assessments/patient/${patientId}`);
      return res.data;
    } catch (error) {
      console.error('Error fetching patient monthly assessments:', error);
      throw error;
    }
  }
};